/**
 * Pricing Rule form modal — create or edit.
 *
 * Sections:
 *   1. Basic — name, strategy selector (with contextual hint)
 *   2. Strategy config — dynamic UI per strategy
 *   3. Applicability — 10 optional filters (connector, source_type, brand,
 *      category, style, sku, color, size, customer_category, min_quantity)
 *   4. Guardrails — min_profit + max_discount_pct
 *   5. Metadata — priority, is_default, is_active, notes
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

const AXIOS_OPTS = { withCredentials: true };

const SOURCE_TYPES = ["supplier", "internal", "custom", "service"];

function toNumOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

function toNumOrZero(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function emptyRule() {
  return {
    name: "",
    strategy: "markup",
    config: { markup_pct: 40 },
    applies_to: {
      connector: "",
      source_type: "",
      brand: "",
      category: "",
      style: "",
      sku: "",
      color: "",
      size: "",
      customer_category_id: "",
      min_quantity: "",
    },
    min_profit: "",
    max_discount_pct: "",
    priority: 0,
    is_default: false,
    is_active: true,
    notes: "",
  };
}

function normalizeIncoming(rule) {
  if (!rule || !rule.name) return emptyRule();
  const a = rule.applies_to || {};
  return {
    ...rule,
    applies_to: {
      connector: a.connector ?? "",
      source_type: a.source_type ?? "",
      brand: a.brand ?? "",
      category: a.category ?? "",
      style: a.style ?? "",
      sku: a.sku ?? "",
      color: a.color ?? "",
      size: a.size ?? "",
      customer_category_id: a.customer_category_id ?? "",
      min_quantity: a.min_quantity ?? "",
    },
    min_profit: rule.min_profit ?? "",
    max_discount_pct: rule.max_discount_pct ?? "",
  };
}

function StrategyConfig({ strategy, config, onChange, t }) {
  if (strategy === "markup") {
    return (
      <div>
        <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
          {t("pricing_form_markup_pct")}
        </label>
        <input
          data-testid={DASHBOARD.pricingRuleFormMarkupPct}
          type="number"
          step="0.01"
          value={config.markup_pct ?? ""}
          onChange={(e) => onChange({ ...config, markup_pct: toNumOrZero(e.target.value) })}
          className="w-full border border-[var(--border)] px-3 py-2"
        />
      </div>
    );
  }
  if (strategy === "fixed_price") {
    return (
      <div>
        <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
          {t("pricing_form_fixed_price")}
        </label>
        <input
          data-testid={DASHBOARD.pricingRuleFormFixedPrice}
          type="number"
          step="0.01"
          value={config.unit_price ?? ""}
          onChange={(e) => onChange({ ...config, unit_price: toNumOrZero(e.target.value) })}
          className="w-full border border-[var(--border)] px-3 py-2"
        />
      </div>
    );
  }
  if (strategy === "formula") {
    return (
      <div>
        <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
          {t("pricing_form_formula_expr")}
        </label>
        <input
          data-testid={DASHBOARD.pricingRuleFormFormula}
          type="text"
          value={config.expression ?? ""}
          onChange={(e) => onChange({ ...config, expression: e.target.value })}
          placeholder="cost * 1.4 + 2"
          className="w-full border border-[var(--border)] px-3 py-2 font-mono-tech text-sm"
        />
      </div>
    );
  }
  if (strategy === "quantity_break") {
    const breaks = Array.isArray(config.breaks) ? config.breaks : [];
    const updateBreak = (idx, patch) => {
      const next = breaks.map((b, i) => (i === idx ? { ...b, ...patch } : b));
      onChange({ ...config, breaks: next });
    };
    const addBreak = () => {
      const last = breaks[breaks.length - 1];
      const nextMin = last ? Number(last.min_quantity || 0) + 12 : 1;
      onChange({
        ...config,
        breaks: [...breaks, { min_quantity: nextMin, markup_pct: 40, label: `${nextMin}+` }],
      });
    };
    const removeBreak = (idx) => {
      onChange({ ...config, breaks: breaks.filter((_, i) => i !== idx) });
    };
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-12 gap-2 text-[0.7rem] font-mono-tech tracking-wider text-[var(--text-secondary)]">
          <div className="col-span-2">{t("pricing_form_break_min_qty")}</div>
          <div className="col-span-3">{t("pricing_form_break_markup")}</div>
          <div className="col-span-3">{t("pricing_form_break_unit_price")}</div>
          <div className="col-span-3">{t("pricing_form_break_label")}</div>
          <div className="col-span-1"></div>
        </div>
        {breaks.map((b, i) => (
          <div
            key={i}
            data-testid={DASHBOARD.pricingRuleFormBreakRow(i)}
            className="grid grid-cols-12 gap-2 items-center"
          >
            <input
              type="number"
              value={b.min_quantity ?? ""}
              onChange={(e) => updateBreak(i, { min_quantity: toNumOrZero(e.target.value) })}
              className="col-span-2 border border-[var(--border)] px-2 py-1.5 text-sm"
            />
            <input
              type="number"
              step="0.01"
              value={b.markup_pct ?? ""}
              onChange={(e) => updateBreak(i, { markup_pct: e.target.value === "" ? null : toNumOrZero(e.target.value) })}
              placeholder="%"
              className="col-span-3 border border-[var(--border)] px-2 py-1.5 text-sm"
            />
            <input
              type="number"
              step="0.01"
              value={b.unit_price ?? ""}
              onChange={(e) => updateBreak(i, { unit_price: e.target.value === "" ? null : toNumOrZero(e.target.value) })}
              placeholder="$"
              className="col-span-3 border border-[var(--border)] px-2 py-1.5 text-sm"
            />
            <input
              type="text"
              value={b.label ?? ""}
              onChange={(e) => updateBreak(i, { label: e.target.value })}
              placeholder="12-71"
              className="col-span-3 border border-[var(--border)] px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              data-testid={DASHBOARD.pricingRuleFormBreakRemove(i)}
              onClick={() => removeBreak(i)}
              className="col-span-1 text-xs text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid={DASHBOARD.pricingRuleFormBreakAdd}
          onClick={addBreak}
          className="font-mono-tech text-xs border border-[var(--border)] px-2.5 py-1.5 hover:bg-black hover:text-white transition-colors"
        >
          {t("pricing_form_break_add")}
        </button>
      </div>
    );
  }
  // passthrough — no config
  return <div className="text-xs text-[var(--text-secondary)] italic">— sin configuración —</div>;
}

export default function PricingRuleFormModal({ open, rule, meta, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyRule);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(normalizeIncoming(rule));
  }, [rule]);

  const isEdit = !!(rule && rule.id);

  const strategyHint = useMemo(() => {
    const key = form.strategy;
    return t(`pricing_form_strategy_hint_${key}`) || "";
  }, [form.strategy, t]);

  const handleStrategyChange = (next) => {
    // Reset config sensibly per strategy.
    const defaults = {
      markup: { markup_pct: 40 },
      quantity_break: {
        breaks: [
          { min_quantity: 1, markup_pct: 100, label: "1-11" },
          { min_quantity: 12, markup_pct: 70, label: "12-71" },
          { min_quantity: 72, markup_pct: 50, label: "72+" },
        ],
      },
      fixed_price: { unit_price: 0 },
      formula: { expression: "cost * 1.4" },
      passthrough: {},
    };
    setForm((f) => ({ ...f, strategy: next, config: defaults[next] || {} }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const applies_to = {};
      Object.entries(form.applies_to).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        applies_to[k] = k === "min_quantity" ? Number(v) : v;
      });
      const payload = {
        name: form.name.trim(),
        strategy: form.strategy,
        config: form.config || {},
        applies_to,
        min_profit: toNumOrNull(form.min_profit),
        max_discount_pct: toNumOrNull(form.max_discount_pct),
        priority: Number(form.priority || 0),
        is_default: !!form.is_default,
        is_active: form.is_active !== false,
        notes: form.notes || "",
      };
      if (isEdit) {
        await axios.put(`${API}/pricing/rules/${rule.id}`, payload, AXIOS_OPTS);
        toast.success("Regla actualizada");
      } else {
        await axios.post(`${API}/pricing/rules`, payload, AXIOS_OPTS);
        toast.success("Regla creada");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const updateFilter = (k, v) => setForm((f) => ({ ...f, applies_to: { ...f.applies_to, [k]: v } }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4">
      <div
        data-testid={DASHBOARD.pricingRuleFormModal}
        className="bg-white w-full max-w-3xl my-8 border border-[var(--border)]"
      >
        <div className="border-b border-[var(--border)] p-5 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="label-overline">{isEdit ? "EDIT" : "NEW"} / RULE</div>
            <h2 className="font-display text-xl font-black tracking-tight">
              {isEdit ? t("pricing_form_edit_title") : t("pricing_form_new_title")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="font-mono-tech text-lg text-[var(--text-secondary)] hover:text-black"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* SECTION 1 — Basic */}
          <div>
            <div className="label-overline mb-3">{t("pricing_form_section_basic")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_rule_name")} *
                </label>
                <input
                  data-testid={DASHBOARD.pricingRuleFormName}
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_rule_strategy")} *
                </label>
                <select
                  data-testid={DASHBOARD.pricingRuleFormStrategy}
                  value={form.strategy}
                  onChange={(e) => handleStrategyChange(e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 bg-white"
                >
                  {(meta.strategies || []).map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label || s.key}
                    </option>
                  ))}
                  {(meta.strategies || []).length === 0 && (
                    <>
                      <option value="markup">Markup %</option>
                      <option value="quantity_break">Quantity Break</option>
                      <option value="fixed_price">Fixed Price</option>
                      <option value="formula">Fórmula</option>
                      <option value="passthrough">Passthrough</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            {strategyHint && (
              <div className="mt-2 text-xs text-[var(--text-secondary)] italic">{strategyHint}</div>
            )}
          </div>

          {/* SECTION 2 — Strategy config */}
          <div>
            <div className="label-overline mb-3">{t("pricing_form_section_strategy")}</div>
            <StrategyConfig
              strategy={form.strategy}
              config={form.config || {}}
              onChange={(c) => setForm({ ...form, config: c })}
              t={t}
            />
          </div>

          {/* SECTION 3 — Filters */}
          <div>
            <div className="label-overline mb-1">{t("pricing_form_section_filters")}</div>
            <p className="text-xs text-[var(--text-secondary)] italic mb-3">
              {t("pricing_form_filter_hint")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FilterInput
                label={t("pricing_filter_connector")}
                testId={DASHBOARD.pricingRuleFormFilterConnector}
                value={form.applies_to.connector}
                onChange={(v) => updateFilter("connector", v)}
                options={meta.connectors.map((c) => ({ value: c.slug, label: c.label }))}
                allowFreeText
              />
              <FilterSelect
                label={t("pricing_filter_source_type")}
                testId={DASHBOARD.pricingRuleFormFilterSourceType}
                value={form.applies_to.source_type}
                onChange={(v) => updateFilter("source_type", v)}
                options={SOURCE_TYPES.map((s) => ({ value: s, label: s }))}
              />
              <FilterInput
                label={t("pricing_filter_brand")}
                testId={DASHBOARD.pricingRuleFormFilterBrand}
                value={form.applies_to.brand}
                onChange={(v) => updateFilter("brand", v)}
                options={meta.brands.map((b) => ({ value: b.name, label: b.name }))}
                allowFreeText
              />
              <FilterInput
                label={t("pricing_filter_category")}
                testId={DASHBOARD.pricingRuleFormFilterCategory}
                value={form.applies_to.category}
                onChange={(v) => updateFilter("category", v)}
                options={meta.categories.map((c) => ({ value: c.name, label: c.name }))}
                allowFreeText
              />
              <FilterInput
                label={t("pricing_filter_style")}
                testId={DASHBOARD.pricingRuleFormFilterStyle}
                value={form.applies_to.style}
                onChange={(v) => updateFilter("style", v)}
              />
              <FilterInput
                label={t("pricing_filter_sku")}
                testId={DASHBOARD.pricingRuleFormFilterSku}
                value={form.applies_to.sku}
                onChange={(v) => updateFilter("sku", v)}
              />
              <FilterInput
                label={t("pricing_filter_color")}
                testId={DASHBOARD.pricingRuleFormFilterColor}
                value={form.applies_to.color}
                onChange={(v) => updateFilter("color", v)}
                options={meta.colors.map((c) => ({ value: c.name, label: c.name }))}
                allowFreeText
              />
              <FilterInput
                label={t("pricing_filter_size")}
                testId={DASHBOARD.pricingRuleFormFilterSize}
                value={form.applies_to.size}
                onChange={(v) => updateFilter("size", v)}
                options={meta.sizes.map((s) => ({ value: s.name, label: s.name }))}
                allowFreeText
              />
              <FilterSelect
                label={t("pricing_filter_customer_category")}
                testId={DASHBOARD.pricingRuleFormFilterCustomerCategory}
                value={form.applies_to.customer_category_id}
                onChange={(v) => updateFilter("customer_category_id", v)}
                options={meta.customer_categories.map((c) => ({ value: c.id, label: c.name }))}
              />
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_filter_min_quantity")}
                </label>
                <input
                  data-testid={DASHBOARD.pricingRuleFormFilterMinQuantity}
                  type="number"
                  value={form.applies_to.min_quantity}
                  onChange={(e) => updateFilter("min_quantity", e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* SECTION 4 — Guardrails */}
          <div>
            <div className="label-overline mb-3">{t("pricing_form_section_clamps")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_rule_min_profit")}
                </label>
                <input
                  data-testid={DASHBOARD.pricingRuleFormMinProfit}
                  type="number"
                  step="0.01"
                  value={form.min_profit}
                  onChange={(e) => setForm({ ...form, min_profit: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2"
                />
                <p className="text-[0.7rem] text-[var(--text-secondary)] mt-1 italic">
                  {t("pricing_form_min_profit_hint")}
                </p>
              </div>
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_rule_max_discount")}
                </label>
                <input
                  data-testid={DASHBOARD.pricingRuleFormMaxDiscount}
                  type="number"
                  step="0.01"
                  value={form.max_discount_pct}
                  onChange={(e) => setForm({ ...form, max_discount_pct: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2"
                />
                <p className="text-[0.7rem] text-[var(--text-secondary)] mt-1 italic">
                  {t("pricing_form_max_discount_hint")}
                </p>
              </div>
            </div>
          </div>

          {/* SECTION 5 — Metadata */}
          <div>
            <div className="label-overline mb-3">{t("pricing_form_section_meta")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                  {t("pricing_rule_priority")}
                </label>
                <input
                  data-testid={DASHBOARD.pricingRuleFormPriority}
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2"
                />
                <p className="text-[0.7rem] text-[var(--text-secondary)] mt-1 italic">
                  {t("pricing_form_priority_hint")}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  data-testid={DASHBOARD.pricingRuleFormIsDefault}
                  type="checkbox"
                  checked={!!form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                  id="pf-is-default"
                />
                <label htmlFor="pf-is-default" className="text-sm">
                  {t("pricing_rule_default")}
                </label>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  data-testid={DASHBOARD.pricingRuleFormIsActive}
                  type="checkbox"
                  checked={form.is_active !== false}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  id="pf-is-active"
                />
                <label htmlFor="pf-is-active" className="text-sm">
                  {t("pricing_rule_active")}
                </label>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
                {t("pricing_rule_notes")}
              </label>
              <textarea
                data-testid={DASHBOARD.pricingRuleFormNotes}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--border)] p-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            data-testid={DASHBOARD.pricingRuleFormCancel}
            onClick={onClose}
            className="font-mono-tech text-sm border border-[var(--border)] px-4 py-2 hover:bg-[var(--bg)]"
          >
            Cancelar
          </button>
          <button
            data-testid={DASHBOARD.pricingRuleFormSubmit}
            onClick={submit}
            disabled={saving}
            className="font-display font-bold text-sm bg-black text-white px-6 py-2 hover:bg-[var(--accent-cyan,#0E5FFF)] transition-colors disabled:opacity-50"
          >
            {saving ? "..." : isEdit ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, testId, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[var(--border)] px-3 py-2 bg-white"
      >
        <option value="">— (todos)</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function FilterInput({ label, testId, value, onChange, options, allowFreeText }) {
  // If options are provided, offer datalist-driven autocomplete but still
  // accept any free-text value (e.g. brand names not yet in the catalog).
  const dlId = testId ? `${testId}-dl` : undefined;
  return (
    <div>
      <label className="block text-xs font-mono-tech tracking-wider text-[var(--text-secondary)] mb-1">
        {label}
      </label>
      <input
        data-testid={testId}
        type="text"
        list={options && options.length > 0 && allowFreeText ? dlId : undefined}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[var(--border)] px-3 py-2"
      />
      {options && options.length > 0 && allowFreeText && (
        <datalist id={dlId}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </datalist>
      )}
    </div>
  );
}
